const STATE = { OFF: "off", RECOG: "recog", LISTEN: "listen" };
const PAUSE_AFTER_SILENCE_MS = 10000;
const STOP_AFTER_SILENCE_MS = 120000;
const VAD_NEED_FRAMES = 3;
const VAD_THRESHOLD = 0.028;

const getSpeechRec = () => window.SpeechRecognition || window.webkitSpeechRecognition || null;
const hasGetUserMedia = () => Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

export const quickSaveVoiceSupported = () => Boolean(getSpeechRec());

const isSecure = () => location.protocol === "https:" || location.hostname === "localhost";

const queryMicPermission = async () => {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return "unknown";
    const permission = await navigator.permissions.query({ name: "microphone" });
    return permission?.state || "unknown";
  } catch (_) {
    return "unknown";
  }
};

const requestWarmMic = () => navigator.mediaDevices.getUserMedia({
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
});

const voiceCmdsPl = [
  { re: /\bnowa linia\b/gi, out: "\n" },
  { re: /\bnowy akapit\b/gi, out: "\n\n" },
  { re: /\bnowy paragraf\b/gi, out: "\n\n" },
  { re: /\bkropka\b/gi, out: ". " },
  { re: /\bprzecinek\b/gi, out: ", " },
  { re: /\b(pytajnik|znak zapytania)\b/gi, out: "? " },
  { re: /\bwykrzyknik\b/gi, out: "! " },
  { re: /\bdwukropek\b/gi, out: ": " },
  { re: /\bśrednik\b/gi, out: "; " },
  { re: /\bmyślnik\b/gi, out: " – " },
  { re: /\botwórz nawias\b/gi, out: "(" },
  { re: /\bzamknij nawias\b/gi, out: ")" },
];

const voiceCmdsEn = [
  { re: /\bnew line\b/gi, out: "\n" },
  { re: /\bnew paragraph\b/gi, out: "\n\n" },
  { re: /\bperiod\b/gi, out: ". " },
  { re: /\bfull stop\b/gi, out: ". " },
  { re: /\bcomma\b/gi, out: ", " },
  { re: /\bquestion mark\b/gi, out: "? " },
  { re: /\bexclamation mark\b/gi, out: "! " },
  { re: /\bcolon\b/gi, out: ": " },
  { re: /\bsemicolon\b/gi, out: "; " },
  { re: /\bdash\b/gi, out: " – " },
];

const normalizeVoiceText = (value, lang) => {
  let next = String(value || "");
  const commands = lang === "en" ? voiceCmdsEn : voiceCmdsPl;
  commands.forEach((command) => {
    next = next.replace(command.re, command.out);
  });
  return next.replace(/\s+([.,!?;:])/g, "$1").replace(/[ \t]{2,}/g, " ");
};

const detectActionCommand = (value, lang) => {
  const next = String(value || "").trim().toLowerCase();
  if (lang === "en") {
    if (next === "undo") return "undo";
    if (next === "clear") return "clear";
    return "";
  }
  if (next === "cofnij" || next === "undo") return "undo";
  if (next === "wyczyść" || next === "wyczysc") return "clear";
  return "";
};

const capitalizeFirstLetter = (value) => {
  const text = String(value || "");
  const match = text.match(/^(\s*["'(]*)([a-ząćęłńóśźż])/i);
  if (!match) return text;
  return match[1] + match[2].toUpperCase() + text.slice(match[0].length);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const createQuickSaveVoiceController = ({ lang, textareaRef, getText, setText, setStatus, setInterim, copy }) => {
  let voiceState = STATE.OFF;
  let rec = null;
  let stream = null;
  let audioCtx = null;
  let analyser = null;
  let vadRaf = 0;
  let silenceTimer = 0;
  let lastActivityAt = 0;
  let vadHot = 0;
  let recStarting = false;
  let recRunning = false;
  let recStopping = false;
  let lastRecStartAt = 0;
  let pendingInterim = "";
  let micAccessGranted = false;
  const insertHistory = [];
  let lastInsertAt = 0;
  const listeners = new Set();

  const emit = () => listeners.forEach((listener) => listener(voiceState));
  const setVoiceState = (next) => {
    voiceState = next;
    emit();
  };

  const setLiveInterim = (value) => {
    if (typeof setInterim !== "function") return;
    setInterim(String(value || ""));
  };

  const speechBlockedMessage = () => copy.speechBlocked || (
    lang === "en"
      ? "This browser allowed the microphone but blocked speech recognition."
      : "Ta przeglądarka dopuściła mikrofon, ale blokuje rozpoznawanie mowy."
  );

  const readText = () => String(typeof getText === "function" ? getText() || "" : "");

  const getRange = (source) => {
    const textarea = textareaRef.current;
    const length = source.length;
    if (!textarea || typeof textarea.selectionStart !== "number" || typeof textarea.selectionEnd !== "number") {
      return { start: length, end: length };
    }
    return {
      start: clamp(textarea.selectionStart, 0, length),
      end: clamp(textarea.selectionEnd, 0, length),
    };
  };

  const applyText = (next, caret = null) => {
    const textarea = textareaRef.current;
    const text = String(next || "");

    if (textarea) {
      textarea.value = text;
    }

    setText(text);

    const nextCaret = typeof caret === "number" ? clamp(caret, 0, text.length) : text.length;
    const restoreCaret = () => {
      try {
        textarea?.focus?.({ preventScroll: true });
        textarea?.setSelectionRange?.(nextCaret, nextCaret);
      } catch (_) {}
    };

    restoreCaret();
    window.requestAnimationFrame(restoreCaret);
  };

  const clearTimers = () => {
    window.clearTimeout(silenceTimer);
    silenceTimer = 0;
  };

  const stopVadLoop = () => {
    if (vadRaf) cancelAnimationFrame(vadRaf);
    vadRaf = 0;
    vadHot = 0;
  };

  const stopStream = () => {
    try { stream?.getTracks?.().forEach((track) => track.stop()); } catch (_) {}
    stream = null;
  };

  const stopAudio = () => {
    stopVadLoop();
    try { audioCtx?.close?.(); } catch (_) {}
    audioCtx = null;
    analyser = null;
  };

  const safeStopRecognition = () => {
    if (!rec || recStopping || !(recRunning || recStarting)) return;
    recStopping = true;
    try { rec.stop(); } catch (_) {}
  };

  const shouldCapitalizeBeforeInsert = (source, start) => {
    if (!source.trim()) return true;
    return /[.!?\n]/.test(source.slice(0, start).slice(-1));
  };

  const insertAtCursor = (rawText) => {
    const source = readText();
    const { start, end } = getRange(source);
    let chunk = normalizeVoiceText(rawText, lang);
    if (shouldCapitalizeBeforeInsert(source, start)) chunk = capitalizeFirstLetter(chunk);

    const before = source.slice(0, start);
    const after = source.slice(end);
    const needsSpace = before.length && !/\s$/.test(before) && !/^[\s.,!?;:)\]\n]/.test(chunk);
    const inserted = `${needsSpace ? " " : ""}${chunk}`;
    const next = before + inserted + after;
    const caret = before.length + inserted.length;

    applyText(next, caret);

    if (inserted.trim()) {
      insertHistory.push({ from: before.length, to: before.length + inserted.length });
      if (insertHistory.length > 30) insertHistory.shift();
      lastInsertAt = Date.now();
    }
  };

  const undoLastInsert = () => {
    const item = insertHistory.pop();
    if (!item) return;
    const value = readText();
    const start = clamp(item.from, 0, value.length);
    const end = clamp(item.to, start, value.length);
    applyText(value.slice(0, start) + value.slice(end), start);
    setStatus(copy.undo);
  };

  const maybeAutoFinishSentence = () => {
    if (!lastInsertAt || Date.now() - lastInsertAt > 20000) return;
    const value = readText();
    const trimmed = value.replace(/[ \t]+$/g, "");
    if (!trimmed || /[.!?\n]$/.test(trimmed)) return;
    if (!/[0-9a-ząćęłńóśźż)\]]$/i.test(trimmed)) return;

    const { start } = getRange(value);
    if (start < value.length - 2) return;
    applyText(`${trimmed}. `, trimmed.length + 2);
  };

  const flushPendingInterim = () => {
    const pending = String(pendingInterim || "").trim();
    if (!pending) return;
    pendingInterim = "";
    setLiveInterim("");
    if (detectActionCommand(pending, lang)) return;
    insertAtCursor(pending);
    setStatus(copy.added);
  };

  const stopAll = (message = copy.stopped) => {
    flushPendingInterim();
    maybeAutoFinishSentence();
    setVoiceState(STATE.OFF);
    clearTimers();
    stopVadLoop();
    safeStopRecognition();
    stopAudio();
    stopStream();
    setLiveInterim("");
    if (message) setStatus(message);
  };

  const bumpActivity = () => {
    lastActivityAt = Date.now();
    if (voiceState !== STATE.RECOG) return;
    clearTimers();
    silenceTimer = window.setTimeout(() => {
      pauseToListen(copy.waiting);
    }, PAUSE_AFTER_SILENCE_MS);
  };

  const canStartRecognition = () => {
    if (Date.now() - lastRecStartAt < 500) return false;
    return !(recStarting || recRunning || recStopping);
  };

  const buildRecognition = () => {
    const SpeechRec = getSpeechRec();
    if (!SpeechRec) return null;
    if (rec) return rec;

    rec = new SpeechRec();
    rec.lang = lang === "en" ? "en-US" : "pl-PL";
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      recStarting = false;
      recStopping = false;
      recRunning = true;
      if (voiceState === STATE.RECOG) setStatus(copy.listening);
    };

    rec.onresult = (event) => {
      bumpActivity();
      let interim = "";
      let finals = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript || "";
        if (result?.isFinal) finals += transcript;
        else interim += transcript;
      }

      interim = String(interim || "").trim();
      if (interim) {
        const live = normalizeVoiceText(interim, lang);
        pendingInterim = interim;
        setLiveInterim(live);
        setStatus(`${copy.hearing || copy.listening} ${live}`.trim());
      }

      finals = String(finals || "").trim();
      if (!finals) return;
      pendingInterim = "";
      setLiveInterim("");
      const command = detectActionCommand(finals, lang);
      if (command === "undo") {
        undoLastInsert();
        return;
      }
      if (command === "clear") {
        applyText("", 0);
        setStatus(copy.clear);
        return;
      }
      insertAtCursor(finals);
      setStatus(copy.added);
    };

    rec.onerror = (event) => {
      const error = String(event?.error || "error");
      setLiveInterim("");
      if (error === "not-allowed" || error === "service-not-allowed") {
        const message = micAccessGranted ? speechBlockedMessage() : copy.blocked;
        stopAll(`${message} (${error})`);
        return;
      }
      if (error === "no-speech") {
        pauseToListen(copy.noSpeech);
        return;
      }
      pauseToListen(`${copy.error}${error}`);
    };

    rec.onend = () => {
      recStarting = false;
      recStopping = false;
      recRunning = false;
      flushPendingInterim();
      if (voiceState === STATE.RECOG) {
        window.setTimeout(() => {
          if (voiceState === STATE.RECOG) startRecognition();
        }, 450);
      }
    };

    return rec;
  };

  function startRecognition(message = "") {
    const recognition = buildRecognition();
    if (!recognition) {
      setStatus(copy.unsupported);
      return;
    }
    stopAudio();
    stopStream();
    setLiveInterim("");
    setVoiceState(STATE.RECOG);
    bumpActivity();
    if (message) setStatus(message);

    if (!canStartRecognition()) {
      window.setTimeout(() => {
        if (voiceState === STATE.RECOG) startRecognition();
      }, 350);
      return;
    }

    recStarting = true;
    lastRecStartAt = Date.now();
    try {
      recognition.start();
    } catch (error) {
      recStarting = false;
      const name = String(error?.name || error?.message || "start_error");
      if (/InvalidStateError/i.test(name)) {
        window.setTimeout(() => {
          if (voiceState === STATE.RECOG) startRecognition();
        }, 650);
        return;
      }
      stopAll(`${copy.error}${name}`);
    }
  }

  const ensureAudioForVad = async () => {
    if (audioCtx && analyser && stream) return true;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === "suspended") await audioCtx.resume();
      const source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      return true;
    } catch (_) {
      stopAudio();
      stopStream();
      return false;
    }
  };

  const startVadLoop = () => {
    if (!analyser) return;
    stopVadLoop();
    const data = new Float32Array(analyser.fftSize);

    const tick = () => {
      if (voiceState !== STATE.LISTEN) {
        stopVadLoop();
        return;
      }
      if (Date.now() - lastActivityAt >= STOP_AFTER_SILENCE_MS) {
        stopAll(copy.stopAfterSilence);
        return;
      }
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let index = 0; index < data.length; index += 1) sum += data[index] * data[index];
      const rms = Math.sqrt(sum / data.length);
      if (rms > VAD_THRESHOLD) {
        vadHot += 1;
        lastActivityAt = Date.now();
      } else {
        vadHot = Math.max(0, vadHot - 1);
      }
      if (vadHot >= VAD_NEED_FRAMES) {
        vadHot = 0;
        stopAudio();
        stopStream();
        startRecognition(copy.resume);
        return;
      }
      vadRaf = requestAnimationFrame(tick);
    };

    vadRaf = requestAnimationFrame(tick);
  };

  async function pauseToListen(message) {
    if (voiceState === STATE.OFF) return;
    flushPendingInterim();
    maybeAutoFinishSentence();
    clearTimers();
    setVoiceState(STATE.LISTEN);
    if (message) setStatus(message);
    safeStopRecognition();
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    if (voiceState !== STATE.LISTEN) return;
    const ok = await ensureAudioForVad();
    if (!ok) {
      stopAll(copy.stopped);
      return;
    }
    startVadLoop();
  }

  const start = async () => {
    if (!quickSaveVoiceSupported() || !hasGetUserMedia()) {
      setStatus(copy.unsupported);
      return;
    }
    if (!isSecure()) {
      setStatus(copy.https);
      return;
    }

    const permission = await queryMicPermission();
    if (permission === "denied") {
      setStatus(`${copy.blocked} (permission=denied)`);
      return;
    }

    let warmPromise = null;
    try {
      setStatus(copy.ask);
      warmPromise = requestWarmMic();
    } catch (error) {
      setStatus(`${copy.blocked} ${String(error?.name || error?.message || "")}`.trim());
      return;
    }

    lastActivityAt = Date.now();
    startRecognition(copy.listening);

    warmPromise.then((warm) => {
      micAccessGranted = true;
      try { warm.getTracks().forEach((track) => track.stop()); } catch (_) {}
    }).catch((error) => {
      micAccessGranted = false;
      const name = String(error?.name || error?.message || "NotAllowedError");
      if (voiceState !== STATE.OFF) stopAll(`${copy.blocked} (${name})`);
      else setStatus(`${copy.blocked} (${name})`);
    });
  };

  const test = async () => {
    if (!hasGetUserMedia()) {
      setStatus(copy.unsupported);
      return;
    }
    if (!isSecure()) {
      setStatus(copy.https);
      return;
    }
    try {
      const permission = await queryMicPermission();
      if (permission === "denied") {
        setStatus(`${copy.blocked} (permission=denied)`);
        return;
      }
      setStatus(copy.ask);
      const testStream = await requestWarmMic();
      micAccessGranted = true;
      testStream.getTracks().forEach((track) => track.stop());
      setStatus(copy.ready);
    } catch (error) {
      micAccessGranted = false;
      setStatus(`${copy.blocked} ${String(error?.name || error?.message || "")}`.trim());
    }
  };

  return {
    toggle() {
      if (voiceState === STATE.OFF) start();
      else stopAll(copy.stopped);
    },
    test,
    destroy() {
      stopAll("");
    },
    onStateChange(listener) {
      listeners.add(listener);
      listener(voiceState);
      return () => listeners.delete(listener);
    },
  };
};
